/**
 * AuditTrailPanel — UEIM-00 + EXP-01H
 *
 * Platform rule: Store technical evidence internally (append-only),
 * display business language to users. No raw UUIDs in normal mode.
 * Technical details (raw JSON + IDs) visible only to users with
 * crm.audit.technical.read capability — never gated on a role string.
 */

import { useState, useEffect } from "react";
import { auditApi, hasCapability } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  UserPlus, UserCheck, Activity, ArrowRightLeft,
  Star, CheckCircle2, Building2, ChevronDown, ChevronUp,
  Clock, AlertCircle, ShieldCheck, Code, ChevronRight,
} from "lucide-react";

// ── Palette ───────────────────────────────────────────────────────────────────
const C = {
  ivory:      "#FBF7F2",
  sand:       "#FAF6F0",
  gold:       "#C9A15F",
  ruby:       "#C64040",
  charcoal:   "#3A3330",
  slate:      "#7A706A",
  divider:    "rgba(201,161,95,0.15)",
  paleGreen:  { bg: "#E8F5EC", text: "#2E7D4F", border: "rgba(46,125,79,0.25)" },
  paleAmber:  { bg: "#FEF3C7", text: "#92400E", border: "rgba(146,64,14,0.20)" },
  paleBlue:   { bg: "#EEF4FF", text: "#0176D3", border: "rgba(1,118,211,0.20)" },
  paleGray:   { bg: "#F4F4F4", text: "#706E6B", border: "rgba(112,110,107,0.20)" },
};

// ── Status code → business label ──────────────────────────────────────────────
const STATUS_LABELS = {
  NEW:                   "New Enquiry",
  CONTACTED:             "Contacted",
  QUALIFYING:            "Qualifying",
  QUALIFIED:             "Qualified",
  POC_REVIEW:            "Planning Ready",
  POC_REVIEW_REQUIRED:   "Manager Review Required",
  CONVERTED:             "Converted",
  CLOSED_LOST:           "Closed Lost",
  CLOSED_WON:            "Closed Won",
  DISQUALIFIED:          "Disqualified",
  OPEN:                  "Open",
  IN_PROGRESS:           "In Progress",
  DONE:                  "Done",
};

// ── Field key → human label ───────────────────────────────────────────────────
const FIELD_LABELS = {
  status:                "Pipeline Stage",
  owner_user_id:         "Relationship Manager",
  assigned_poc_id:       "Assigned RM",
  account_id:            "Account",
  contact_id:            "Contact",
  opportunity_id:        "Opportunity",
  opportunity_number:    "Opportunity No.",
  qualification_status:  "Qualification Status",
  event_date:            "Event Date",
  event_type:            "Event Type",
  guest_count:           "Guest Count",
  budget_range:          "Budget Range",
  source:                "Lead Source",
};

// ── UUID / internal-ID detection ──────────────────────────────────────────────
const UUID_RE = /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi;
const INTERNAL_USER_RE = /\buat-user-\d{3}-\w+\b/gi;
// Matches internal entity slugs: lead-bme-xxx-xxx, lead-uat-xxx, etc.
const INTERNAL_LEAD_ID_RE = /\blead-[a-z]{2,8}-[a-z0-9][a-z0-9-]*/gi;

function isTechnicalId(val) {
  if (typeof val !== "string") return false;
  UUID_RE.lastIndex = 0;
  if (UUID_RE.test(val)) return true;
  if (/^uat-(user|demo|admin|lead|bme)-/i.test(val)) return true;
  return false;
}

// ── Summary sanitizer ─────────────────────────────────────────────────────────
function sanitizeSummary(summary, action) {
  if (!summary) return "";

  // Action-specific rewrites (cheapest first)
  if (action === "ENTITY_FOLLOWED")   return "Followed this lead";
  if (action === "ENTITY_UNFOLLOWED") return "Unfollowed this lead";
  if (action === "LEAD_UPDATED")      return "Lead information updated";
  if (action === "TASK_CREATED" || action === "NOTE_CREATED")
    return "Note added to this lead";
  if (action === "LEAD_ASSIGNED" || action === "LEAD_OWNER_ASSIGNED")
    return "Relationship Manager assigned to this lead";
  if (action === "LEAD_CLAIMED")       return "Relationship Manager claimed this lead";
  if (action === "LEAD_ASSIGNED_LEGACY") return "Relationship Manager assignment updated";
  if (action === "POC_ASSIGNED")       return "Planning role assigned to this lead";

  let s = summary;

  // Remove [LEGACY] prefix
  s = s.replace(/^\[LEGACY\]\s*/i, "Legacy — ").trim();

  // Remove raw LEAD/xxx entity paths
  s = s.replace(/\bLEAD\/[a-z0-9-]+/gi, "this lead");

  // Strip raw UUIDs
  UUID_RE.lastIndex = 0;
  s = s.replace(UUID_RE, "");

  // Strip internal lead slugs (lead-bme-xxx, lead-uat-xxx, uat-lead-xxx etc.)
  INTERNAL_LEAD_ID_RE.lastIndex = 0;
  s = s.replace(INTERNAL_LEAD_ID_RE, "this lead");

  // Strip internal user IDs (uat-user-003-sales style)
  s = s.replace(INTERNAL_USER_RE, "");

  // Translate pipeline status codes to business labels
  s = s.replace(
    /\b(NEW|CONTACTED|QUALIFYING|QUALIFIED|POC_REVIEW_REQUIRED|POC_REVIEW|CONVERTED|CLOSED_LOST|CLOSED_WON|DISQUALIFIED)\b/g,
    (m) => STATUS_LABELS[m] || m,
  );

  // Translate source tokens
  s = s.replace(/\bMANUAL_ENTRY\b/g, "Manual Entry");
  s = s.replace(/\bBUILD_MY_EVENT\b/g, "Build My Event");
  s = s.replace(/\bEXPLORE_PAGE\b/g,  "Explore Page");

  // Clean trailing artefacts from stripping
  s = s.replace(/\s{2,}/g, " ").replace(/,\s*$/, "").replace(/→\s*$/, "").trim();
  return s;
}

// ── Special content renderers ─────────────────────────────────────────────────

/** LEAD_CONVERTED: extract account/opportunity from summary and show a structured card */
function ConversionCard({ event }) {
  const summary     = event.summary || "";
  const accountName = summary.match(/Account:\s*([^,]+)/)?.[1]?.trim();
  const oppNumber   = summary.match(/Opportunity:\s*([A-Z0-9-]+)/)?.[1]
                      || event.after_state?.opportunity_number;

  if (!accountName && !oppNumber) return null;
  return (
    <div
      className="mt-2 rounded-lg px-3 py-2.5 flex flex-col gap-1"
      style={{ background: C.paleGreen.bg, border: `1px solid ${C.paleGreen.border}` }}
    >
      {accountName && (
        <div className="flex items-center gap-2 text-xs">
          <Building2 size={11} style={{ color: C.paleGreen.text }} />
          <span style={{ color: C.slate }}>Account:</span>
          <span className="font-medium" style={{ color: C.charcoal }}>{accountName}</span>
        </div>
      )}
      {oppNumber && (
        <div className="flex items-center gap-2 text-xs">
          <CheckCircle2 size={11} style={{ color: C.paleGreen.text }} />
          <span style={{ color: C.slate }}>Opportunity:</span>
          <span className="font-medium" style={{ color: C.charcoal }}>{oppNumber}</span>
        </div>
      )}
    </div>
  );
}

/** LEAD_STATUS_CHANGED: parse FROM → TO and render a visual pipeline transition */
function StatusTransitionBadge({ summary }) {
  const match = summary?.match(/transitioned\s+([\w_]+)\s*→\s*([\w_]+)/i)
              || summary?.match(/(Planning Ready|Qualified|New Enquiry|Contacted|Qualifying|Converted)[^→]*→\s*([A-Za-z _]+)/i)
              || summary?.match(/([\w_]+)\s*→\s*([\w_]+)/);
  if (!match) return null;
  const from = STATUS_LABELS[match[1]] || match[1].replace(/_/g, " ");
  const to   = STATUS_LABELS[match[2]] || match[2].replace(/_/g, " ");
  return (
    <div className="mt-2 flex items-center gap-1.5 text-xs">
      <span
        className="px-2 py-0.5 rounded text-[11px] font-medium"
        style={{ background: C.paleAmber.bg, color: C.paleAmber.text, border: `1px solid ${C.paleAmber.border}` }}
      >{from}</span>
      <ChevronRight size={11} style={{ color: C.slate }} />
      <span
        className="px-2 py-0.5 rounded text-[11px] font-medium"
        style={{ background: C.paleGreen.bg, color: C.paleGreen.text, border: `1px solid ${C.paleGreen.border}` }}
      >{to}</span>
    </div>
  );
}

/** Before/after state with human labels — filters out UUID values and system fields */
function StateDisplay({ label, state }) {
  if (!state || !Object.keys(state).length) return null;
  const filtered = Object.entries(state).filter(([k, v]) => {
    if (isTechnicalId(String(v ?? ""))) return false;
    if (["updated_at","created_at","updated_by","created_by","version","tenant_id"].includes(k)) return false;
    return true;
  });
  if (!filtered.length) return null;
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wide mb-1" style={{ color: C.slate }}>
        {label}
      </p>
      <div className="space-y-0.5">
        {filtered.map(([k, v]) => (
          <div key={k} className="flex items-center gap-1 text-[11px]">
            <span style={{ color: C.slate }}>{FIELD_LABELS[k] || k.replace(/_/g, " ")}:</span>
            <span className="font-medium" style={{ color: C.charcoal }}>
              {STATUS_LABELS[String(v)] || String(v ?? "—")}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Audit event metadata ──────────────────────────────────────────────────────
const AUDIT_META = {
  LEAD_CREATED:               { label: "Lead Created",               category: "System",   icon: UserPlus,       color: C.paleBlue  },
  PROVISIONAL_LEAD_CREATED:   { label: "Enquiry Received",           category: "Customer", icon: UserPlus,       color: C.paleGreen },
  LEAD_STATUS_CHANGED:        { label: "Status Changed",             category: "CRM",      icon: ArrowRightLeft, color: C.paleGray  },
  LEAD_ASSIGNED:              { label: "RM Assigned",                category: "CRM",      icon: UserCheck,      color: C.paleBlue  },
  LEAD_OWNER_ASSIGNED:        { label: "RM Assigned",                category: "CRM",      icon: UserCheck,      color: C.paleBlue  },
  LEAD_CLAIMED:               { label: "RM Claimed Lead",            category: "CRM",      icon: UserCheck,      color: C.paleBlue  },
  LEAD_QUALIFIED:             { label: "Qualification Updated",      category: "System",   icon: Star,           color: C.paleGreen },
  LEAD_CONVERTED:             { label: "Lead Converted",             category: "CRM",      icon: CheckCircle2,   color: C.paleGreen },
  LEAD_REVIEWED:              { label: "Event Intent Reviewed",      category: "CRM",      icon: ShieldCheck,    color: C.paleAmber },
  LEAD_UPDATED:               { label: "Lead Updated",               category: "CRM",      icon: Activity,       color: C.paleGray  },
  LEAD_ASSIGNED_LEGACY:       { label: "RM Assignment Updated",      category: "CRM",      icon: UserCheck,      color: C.paleBlue  },
  POC_ASSIGNED:               { label: "Planning Role Assigned",     category: "CRM",      icon: UserCheck,      color: C.paleBlue  },
  TASK_CREATED:               { label: "Note Added",                 category: "CRM",      icon: Activity,       color: C.paleGray  },
  NOTE_CREATED:               { label: "Note Added",                 category: "CRM",      icon: Activity,       color: C.paleGray  },
  ENTITY_FOLLOWED:            { label: "Lead Followed",              category: "System",   icon: Activity,       color: C.paleGray  },
  ENTITY_UNFOLLOWED:          { label: "Lead Unfollowed",            category: "System",   icon: Activity,       color: C.paleGray  },
  "contact.update":           { label: "Contact Updated",            category: "CRM",      icon: UserCheck,      color: C.paleGray  },
  "account.update":           { label: "Account Updated",            category: "CRM",      icon: Building2,      color: C.paleGray  },
  "opportunity.owner_changed":{ label: "Opportunity Owner Changed",  category: "CRM",      icon: UserCheck,      color: C.paleBlue  },
};

function getMeta(action) {
  const m = AUDIT_META[action];
  if (!m) return {
    label: action.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase()),
    category: "System",
    icon: Activity,
    color: C.paleGray,
  };
  return m;
}

// ── Filter groups ──────────────────────────────────────────────────────────────
const FILTERS = [
  { label: "All",      value: null },
  { label: "Customer", value: new Set(["PROVISIONAL_LEAD_CREATED"]) },
  { label: "CRM",      value: new Set([
      "LEAD_ASSIGNED","LEAD_OWNER_ASSIGNED","LEAD_CLAIMED",
      "LEAD_STATUS_CHANGED","LEAD_CONVERTED","LEAD_REVIEWED",
      "LEAD_UPDATED","LEAD_ASSIGNED_LEGACY","POC_ASSIGNED",
      "opportunity.owner_changed","contact.update","account.update",
    ]) },
  { label: "System",   value: new Set([
      "LEAD_CREATED","LEAD_QUALIFIED","ENTITY_FOLLOWED",
      "ENTITY_UNFOLLOWED","TASK_CREATED","NOTE_CREATED",
    ]) },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtTs(isoStr) {
  if (!isoStr) return "";
  try {
    const d = new Date(isoStr);
    return (
      d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
      + "  "
      + d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })
    );
  } catch { return isoStr; }
}

function extractActorName(event) {
  if (event.actor_email) {
    const parts = event.actor_email.split("@")[0].split(/[.\-_]/);
    return parts.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(" ");
  }
  if (event.actor_id) {
    const id = event.actor_id;
    if (id === "system" || id.startsWith("sys-") || id === "SYSTEM") return "System";
    return "CRM User";
  }
  return "System";
}

// ── Single audit row ───────────────────────────────────────────────────────────
function AuditRow({ event, canSeeTechnical }) {
  const [showTech, setShowTech] = useState(false);
  const meta           = getMeta(event.action);
  const IconComp       = meta.icon;
  const actor          = extractActorName(event);
  const cleanedSummary = sanitizeSummary(event.summary, event.action);
  const hasRawDetails  = event.before_state || event.after_state;
  const showDiff       = hasRawDetails
    && event.action !== "ENTITY_FOLLOWED"
    && event.action !== "ENTITY_UNFOLLOWED";

  return (
    <div className="border-b last:border-b-0" style={{ borderColor: C.divider }}>
      <div className="flex items-start gap-3 px-4 py-3">

        {/* Category icon */}
        <div
          className="mt-0.5 w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: meta.color.bg, border: `1px solid ${meta.color.border}` }}
        >
          <IconComp size={13} style={{ color: meta.color.text }} />
        </div>

        <div className="flex-1 min-w-0">

          {/* Action label — human business name only */}
          <p className="text-sm font-semibold leading-snug" style={{ color: C.charcoal }}>
            {meta.label}
          </p>

          {/* Sanitized summary — no raw IDs, no status codes, no snake_case */}
          {cleanedSummary && (
            <p className="text-xs mt-0.5 leading-relaxed" style={{ color: C.slate }}>
              {cleanedSummary}
            </p>
          )}

          {/* Structured enrichment cards */}
          {event.action === "LEAD_CONVERTED"      && <ConversionCard event={event} />}
          {event.action === "LEAD_STATUS_CHANGED"  && <StatusTransitionBadge summary={event.summary} />}

          {/* Human-readable before/after diff — UUIDs filtered out */}
          {showDiff && (
            <div
              className="mt-2 rounded-lg p-2.5 space-y-2 text-[11px]"
              style={{ background: C.sand, border: `1px solid ${C.divider}` }}
              data-testid={`audit-diff-${event.id}`}
            >
              <StateDisplay label="Before" state={event.before_state} />
              <StateDisplay label="After"  state={event.after_state}  />
            </div>
          )}

          {/* Meta row: category pill · actor · timestamp · (Technical toggle for crm.audit.technical.read) */}
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <span
              className="text-[10px] px-2 py-0.5 rounded-full font-medium"
              style={{ background: meta.color.bg, color: meta.color.text, border: `1px solid ${meta.color.border}` }}
            >
              {meta.category}
            </span>
            <span className="text-[10px]" style={{ color: C.slate }}>{actor}</span>
            <span className="text-[10px]" style={{ color: C.slate }}>·</span>
            <span className="text-[10px]" style={{ color: C.slate }}>{fmtTs(event.timestamp)}</span>

            {/* Technical Details toggle — crm.audit.technical.read only */}
            {canSeeTechnical && hasRawDetails && (
              <button
                onClick={() => setShowTech(v => !v)}
                className="text-[10px] flex items-center gap-0.5 hover:underline ml-auto"
                style={{ color: C.slate }}
                data-testid={`audit-tech-toggle-${event.id}`}
              >
                <Code size={10} />
                {" Technical "}
                {showTech ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
              </button>
            )}
          </div>

          {/* Technical details drawer — raw evidence, visible only to crm.audit.technical.read */}
          {canSeeTechnical && showTech && hasRawDetails && (
            <div
              className="mt-2 rounded-lg p-2.5 text-[10px] font-mono overflow-x-auto"
              style={{ background: "#1e1e2e", color: "#cdd6f4", border: "1px solid #45475a" }}
              data-testid={`audit-tech-detail-${event.id}`}
            >
              <div style={{ color: "#6c7086" }}>Event ID: {event.id}</div>
              <div style={{ color: "#6c7086" }}>Action: {event.action}</div>
              {event.before_state && (
                <pre className="mt-1 whitespace-pre-wrap" style={{ color: "#a6e3a1" }}>
{"BEFORE: " + JSON.stringify(event.before_state, null, 2)}
                </pre>
              )}
              {event.after_state && (
                <pre className="mt-1 whitespace-pre-wrap" style={{ color: "#89b4fa" }}>
{"AFTER: " + JSON.stringify(event.after_state, null, 2)}
                </pre>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────
export function AuditTrailPanel({ leadId }) {
  const { user } = useAuth();
  // Capability gate — never hardcoded role string
  const canSeeTechnical = hasCapability(user, "crm.audit.technical.read");

  const [events,  setEvents]  = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [filter,  setFilter]  = useState(null); // null = All

  useEffect(() => {
    if (!leadId) return;
    setLoading(true);
    setError(null);
    auditApi
      .listAudit({ entity_type: "LEAD", entity_id: leadId, limit: 100 })
      .then(r => {
        const data = r.data;
        setEvents(Array.isArray(data) ? data : (data.results || []));
      })
      .catch(() => setError("Unable to load audit trail."))
      .finally(() => setLoading(false));
  }, [leadId]);

  const visible = filter === null
    ? events
    : events.filter(e => {
        const f = FILTERS.find(fg => fg.value === filter);
        return f && f.value && f.value.has(e.action);
      });

  return (
    <div
      data-testid="audit-trail-panel"
      className="rounded-xl overflow-hidden"
      style={{ background: C.ivory, border: `1px solid ${C.divider}` }}
    >
      {/* Header */}
      <div
        className="px-4 py-3 flex items-center justify-between"
        style={{ borderBottom: `1px solid ${C.divider}` }}
      >
        <div className="flex items-center gap-2">
          <ShieldCheck size={14} style={{ color: C.gold }} />
          <h3 className="text-sm font-semibold" style={{ color: C.charcoal }}>Audit Trail</h3>
          {events.length > 0 && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
              style={{ background: `${C.gold}18`, color: C.gold, border: `1px solid ${C.gold}30` }}
            >
              {events.length}
            </span>
          )}
        </div>
      </div>

      {/* Filter tabs */}
      <div
        className="flex gap-1 px-4 py-2 overflow-x-auto"
        style={{ borderBottom: `1px solid ${C.divider}` }}
      >
        {FILTERS.map(f => (
          <button
            key={f.label}
            data-testid={`audit-filter-${f.label.toLowerCase()}`}
            onClick={() => setFilter(f.value)}
            className="text-[11px] font-medium px-2.5 py-1 rounded-full whitespace-nowrap transition-colors"
            style={{
              background: filter === f.value ? C.gold : "transparent",
              color:      filter === f.value ? "#fff" : C.slate,
              border:     `1px solid ${filter === f.value ? C.gold : C.divider}`,
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Body */}
      <div style={{ maxHeight: "320px", overflowY: "auto" }}>
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Clock size={16} className="animate-spin mr-2" style={{ color: C.slate }} />
            <span className="text-xs" style={{ color: C.slate }}>Loading audit trail…</span>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 px-4 py-4">
            <AlertCircle size={14} style={{ color: C.ruby }} />
            <span className="text-xs" style={{ color: C.ruby }}>{error}</span>
          </div>
        )}

        {!loading && !error && visible.length === 0 && (
          <div className="px-4 py-6 text-center">
            <ShieldCheck size={20} className="mx-auto mb-2 opacity-30" style={{ color: C.slate }} />
            <p className="text-xs" style={{ color: C.slate }}>No audit events recorded yet.</p>
          </div>
        )}

        {!loading && !error && visible.map(ev => (
          <AuditRow
            key={ev.id || ev._id}
            event={ev}
            canSeeTechnical={canSeeTechnical}
          />
        ))}
      </div>
    </div>
  );
}

export default AuditTrailPanel;
